/** @jsxImportSource ./simboluri-jsx */
// LINKUL UNEI MĂRCI — `<MarcaLink marca="3g" …/>` oriunde apare pe ecran o
// marcă de bară / etrier (componente, inventar, extras, nota de calcul): un
// link spre pagina mărcii, cu un cartonaș de hover care spune ce e (familia,
// descrierea tipului) și îi arată forma, desenată mic din același
// `DrawingData` pe care pagina mărcii îl randează mare.
//
// Fișier de hover: fără pragma de simboluri (v. structure/CLAUDE.md).

import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { fetchDrawingUrl, planArmareMarcaUrl } from "./apiStructura";
import HoverCard from "./HoverCard";
import MiniDesen from "./MiniDesen";
import { citesteRef, hashMarca } from "./legaturi";
import { NUME_FAMILIE_MARCA, citesteMarca } from "./marci";
import type { DrawingData } from "./tipuri";

/** Forma mărcii, adusă abia când cartonașul se deschide; un 404 e «forma nu
 *  e desenată încă», nu o eroare. */
function FormaMarcii({ src, versiune }: { src: string; versiune?: string | null }) {
  const t = useT();
  // `undefined` = pe drum; `null` = nu există
  const [desen, setDesen] = useState<DrawingData | null | undefined>(undefined);
  useEffect(() => {
    let viu = true;
    fetchDrawingUrl(src, versiune)
      .then((d) => { if (viu) setDesen(d); })
      .catch(() => { if (viu) setDesen(null); });
    return () => { viu = false; };
  }, [src, versiune]);
  if (desen === undefined) {
    return <span className="sg-muted" style={{ fontSize: 12 }}>{t("comun.se_incarca")}</span>;
  }
  if (desen === null) {
    return <span className="sg-muted" style={{ fontSize: 12 }}>{t("struct.pa.forma_lipsa")}</span>;
  }
  return <MiniDesen desen={desen} latime={300} inaltime={140} />;
}

export default function MarcaLink({ marca, projectId, solutie, cloneId, descriere, versiune }: {
  marca: string;
  projectId: string;
  /** referința de legătură a rândului privit (v. `legaturi.refLegatura`) */
  solutie?: string | null;
  /** spațiul din care se citește forma; lipsă → rândul referinței */
  cloneId?: string | null;
  /** descrierea tipului (Ø, forma, dimensiunile), când cine leagă o știe */
  descriere?: string | null;
  /** versiunea setului de planșe, cheia memoriei desenului */
  versiune?: string | null;
}) {
  const t = useT();
  const spatiu = cloneId ?? citesteRef(solutie).sol;
  const citita = citesteMarca(marca);
  const continut = (
    <span style={{ display: "block" }}>
      <span style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <b className="sg-mono">{marca}</b>
        {citita && (
          <span className="sg-muted" style={{ fontSize: 12 }}>
            {t(NUME_FAMILIE_MARCA[citita.familie])}
          </span>
        )}
      </span>
      {descriere && <span style={{ display: "block", fontSize: 12, marginBottom: 6 }}>{descriere}</span>}
      {spatiu && (
        <FormaMarcii src={planArmareMarcaUrl(projectId, spatiu, marca)} versiune={versiune} />
      )}
      <span className="sg-muted" style={{ display: "block", fontSize: 11, marginTop: 6 }}>
        {t("struct.pa.marca_deschide")}
      </span>
    </span>
  );
  return (
    <HoverCard continut={continut} latime={340} eticheta={t("struct.pa.marca_eticheta", { marca })}>
      <a className="sg-mono ob-marca" href={hashMarca(projectId, marca, solutie)}
         data-testid="marca-link">
        {marca}
      </a>
    </HoverCard>
  );
}
